import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { getToken } from "@/utils/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGate from "@/components/RoleGate";
import Lucide from "@/components/Base/Lucide";
import Button from "@/components/Base/Button";
import Table from "@/components/Base/Table";
import { Menu } from "@/components/Base/Headless";
import axios from "axios";
import PageLoadingOverlay from "@/components/PageLoadingOverlay";

type ReceiptLine = { productId: string; quantity: number; product?: { id: string; name: string; sku: string | null } };
type Receipt = { id: string; batchNo?: string | null; teamId: string; distributorId?: string | null; outAt: string; team: { id: string; name: string } | null; lines: ReceiptLine[] };
type StockInReceipt = { id: string; receivedAt: string; stockInType?: string | null; lines: Array<{ productId: string; quantity: number }> };
type Distributor = { id: string; name: string; supplyTo?: string[]; region?: string };
type Team = { id: string; name: string };
type Region = { id: string; name: string };
type ProductRow = { id: string; sku: string; name: string };
type TeamColumn = {
  distributorId: string;
  distributorName: string;
  teamId: string;
  teamName: string;
  /** Receipt exists for this distributor+team, but the team is no longer on supplyTo. */
  orphan?: boolean;
};

function teamColKey(distributorId: string, teamId: string): string {
  return `${distributorId}::${teamId}`;
}

function receiptTeamColKey(r: Receipt, firstDistributorByTeamId: Map<string, string>): string | null {
  const teamId = r.teamId || r.team?.id;
  if (!teamId) return null;
  const distId = r.distributorId || firstDistributorByTeamId.get(teamId) || "";
  if (!distId) return null;
  return teamColKey(distId, teamId);
}

const STORAGE_KEY_FILTER = "stockledger_stockOutTable_filter";
const STORAGE_KEY_SORT = "stockledger_stockOutTable_sort";
const STORAGE_KEY_MONTH = "stockledger_stockOutTable_month";
const STORAGE_KEY_REGION = "stockledger_stockOutTable_region";
const PRODUCT_TYPE_ORDER = ["Devices", "Refills", "Accessories"];

function loadSavedFilter(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_FILTER);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.every((x: unknown) => typeof x === "string") ? parsed : [];
  } catch {
    return [];
  }
}

function loadSavedSortOrder(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_SORT);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.every((x: unknown) => typeof x === "string") ? parsed : [];
  } catch {
    return [];
  }
}

function loadSavedMonth(fallback: string): string {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_MONTH);
    if (!raw || typeof raw !== "string") return fallback;
    return /^\d{4}-\d{2}$/.test(raw) ? raw : fallback;
  } catch {
    return fallback;
  }
}

function loadSavedRegion(): string {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_REGION);
    if (!raw || typeof raw !== "string") return "";
    return raw.trim();
  } catch {
    return "";
  }
}

const COL_NO_PX = 44;
const COL_SKU_PX = 90;
const COL_PRODUCT_PX = 200;
const COL_TEAM_PX = 108;
const COL_EXTRA_PX = 90;

function colStyle(w: number): React.CSSProperties {
  return { width: w, minWidth: w, maxWidth: w };
}

const formatInt = (n: number) => n.toLocaleString("en-US");
const formatIntOrDash = (n: number) => (n === 0 ? "-" : formatInt(n));

/** Get ISO week number (1-53) for a date. */
function getISOWeek(d: Date): number {
  const date = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const day = date.getUTCDay() || 7; // ISO: Monday=1 ... Sunday=7
  date.setUTCDate(date.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

function getISOWeekRange(year: number, week: number): { start: Date; end: Date } {
  const jan4 = new Date(year, 0, 4);
  const jan4Day = jan4.getDay();
  const mondayOffset = jan4Day === 0 ? -6 : 1 - jan4Day;
  const week1Monday = new Date(year, 0, 4 + mondayOffset);
  const start = new Date(week1Monday);
  start.setDate(week1Monday.getDate() + (week - 1) * 7);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

function formatDateRange(start: Date, end: Date): string {
  const opts: Intl.DateTimeFormatOptions = { day: "2-digit", month: "short", year: "numeric" };
  return `${start.toLocaleDateString("en-GB", opts)} - ${end.toLocaleDateString("en-GB", opts)}`;
}

export default function StockOutListPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const companyIdFromUrl = searchParams.get("companyId");
  const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
  const token = getToken();

  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [selectedMonth, setSelectedMonth] = useState<string>(() => loadSavedMonth(defaultMonth));
  const [pickerYear, setPickerYear] = useState(() => Number((loadSavedMonth(defaultMonth) || defaultMonth).slice(0, 4)));
  const [regions, setRegions] = useState<Region[]>([]);
  const [selectedRegionId, setSelectedRegionId] = useState<string>(loadSavedRegion);
  const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  useEffect(() => {
    setPickerYear(Number(selectedMonth.slice(0, 4)));
  }, [selectedMonth]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${apiUrl}/regions?pageSize=500`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        setRegions(list.filter((r: Region) => r.id && r.name));
      } catch {
        setRegions([]);
      }
    })();
  }, [apiUrl, token]);

  const selectedMonthInfo = useMemo(() => {
    const [y, m] = selectedMonth.split("-").map(Number);
    const start = new Date(y, m - 1, 1);
    const end = new Date(y, m, 0);
    const startStr = `${y}-${String(m).padStart(2, "0")}-01`;
    const endStr = `${y}-${String(m).padStart(2, "0")}-${String(end.getDate()).padStart(2, "0")}`;
    const label = new Date(`${selectedMonth}-01`).toLocaleString("default", { month: "long", year: "numeric" });
    return { start, end, startStr, endStr, label };
  }, [selectedMonth]);

  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [stockInReceipts, setStockInReceipts] = useState<StockInReceipt[]>([]);
  const [openingByProduct, setOpeningByProduct] = useState<Record<string, number>>({});
  const [productIdToType, setProductIdToType] = useState<Record<string, string>>({});
  const [allProducts, setAllProducts] = useState<ProductRow[]>([]);
  const [selectedProductTypeFilters, setSelectedProductTypeFilters] = useState<string[]>(loadSavedFilter);
  const [productTypeCustomOrder, setProductTypeCustomOrder] = useState<string[]>(loadSavedSortOrder);
  const [loading, setLoading] = useState(true);

  const queryCompany = companyIdFromUrl ? `?companyId=${companyIdFromUrl}` : "";

  const addUrl = companyIdFromUrl ? `/stock-out/add?companyId=${companyIdFromUrl}` : "/stock-out/add";

  /**
   * Columns = current supplyTo, plus leftover receipts whose team is no longer on that
   * distributor's supply list (so they stay visible and deletable).
   */
  const { teamColumns, distributorSpans, firstDistributorByTeamId } = useMemo(() => {
    const firstByTeam = new Map<string, string>();
    const teamByName = new Map(teams.map((t) => [(t.name || "").trim(), t]));
    const teamById = new Map(teams.map((t) => [t.id, t]));
    const distById = new Map(distributors.map((d) => [d.id, d]));
    const sortedDist = [...distributors].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    const colsByDistId = new Map<string, TeamColumn[]>();

    for (const d of sortedDist) {
      const supplyTo = (d.supplyTo || []).map((n) => String(n).trim()).filter(Boolean);
      const distTeams: TeamColumn[] = [];
      for (const name of supplyTo) {
        const team = teamByName.get(name);
        if (team) {
          if (!firstByTeam.has(team.id)) firstByTeam.set(team.id, d.id);
          distTeams.push({ distributorId: d.id, distributorName: d.name, teamId: team.id, teamName: team.name });
        }
      }
      colsByDistId.set(d.id, distTeams);
    }

    const existingKeys = new Set<string>();
    colsByDistId.forEach((cols) => {
      cols.forEach((c) => existingKeys.add(teamColKey(c.distributorId, c.teamId)));
    });

    for (const r of receipts) {
      const teamId = r.teamId || r.team?.id;
      if (!teamId) continue;
      const distId = r.distributorId || firstByTeam.get(teamId) || "";
      if (!distId || !distById.has(distId)) continue;
      const key = teamColKey(distId, teamId);
      if (existingKeys.has(key)) continue;
      const dist = distById.get(distId);
      const team = teamById.get(teamId) || r.team;
      if (!dist || !team) continue;
      existingKeys.add(key);
      const list = colsByDistId.get(distId) ?? [];
      list.push({
        distributorId: dist.id,
        distributorName: dist.name,
        teamId: team.id,
        teamName: team.name,
        orphan: true,
      });
      colsByDistId.set(distId, list);
    }

    const teamCols: TeamColumn[] = [];
    const spans: { name: string; span: number }[] = [];
    for (const d of sortedDist) {
      const distTeams = colsByDistId.get(d.id) ?? [];
      if (distTeams.length > 0) {
        spans.push({ name: d.name, span: distTeams.length });
        teamCols.push(...distTeams);
      }
    }
    return { teamColumns: teamCols, distributorSpans: spans, firstDistributorByTeamId: firstByTeam };
  }, [distributors, teams, receipts]);

  useEffect(() => {
    if (!selectedRegionId.trim()) {
      setDistributors([]);
      setReceipts([]);
      setStockInReceipts([]);
      setOpeningByProduct({});
      setLoading(false);
      return;
    }
    (async () => {
      try {
        setLoading(true);
        const weekTableUrl = `${apiUrl}/stock-out/week-table?start=${selectedMonthInfo.startStr}&end=${selectedMonthInfo.endStr}`;
        const regionParam = `&regionId=${encodeURIComponent(selectedRegionId)}`;
        const [distRes, teamsRes, weekRes, typesRes, productsRes, stockInMonthRes, openingRes] = await Promise.all([
          axios.get(`${apiUrl}/distributors?pageSize=500${regionParam}${queryCompany}`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${apiUrl}/teams?pageSize=500${queryCompany}`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(weekTableUrl, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${apiUrl}/product-types?pageSize=200`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${apiUrl}/products?pageSize=500`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${apiUrl}/stock-in/month-table?month=${selectedMonth}${regionParam}`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${apiUrl}/stock-in/opening-balances?asOf=${encodeURIComponent(`${selectedMonthInfo.startStr}T00:00:00.000Z`)}`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        setDistributors(Array.isArray(distRes.data?.data) ? distRes.data.data : []);
        setTeams(Array.isArray(teamsRes.data?.data) ? teamsRes.data.data : []);
        setReceipts(Array.isArray(weekRes.data?.data) ? weekRes.data.data : []);
        setStockInReceipts(Array.isArray(stockInMonthRes.data?.data) ? stockInMonthRes.data.data : []);
        if (Array.isArray(openingRes.data)) {
          const map: Record<string, number> = {};
          openingRes.data.forEach((row: { productId: string; balance: number }) => {
            if (row?.productId) map[row.productId] = row.balance ?? 0;
          });
          setOpeningByProduct(map);
        } else {
          setOpeningByProduct({});
        }
        const prods = Array.isArray(productsRes.data?.data) ? productsRes.data.data : [];
        const idToType: Record<string, string> = {};
        const mappedProducts: ProductRow[] = [];
        prods.forEach((p: { id: string; type?: string; sku?: string | null; name?: string | null }) => {
          if (p.id && (p.type || "").trim()) idToType[p.id] = String(p.type).trim();
          if (p.id) {
            mappedProducts.push({
              id: p.id,
              sku: p.sku ?? "",
              name: p.name ?? "",
            });
          }
        });
        setProductIdToType(idToType);
        setAllProducts(mappedProducts);
      } catch (e) {
        console.error(e);
        setReceipts([]);
        setStockInReceipts([]);
        setOpeningByProduct({});
      } finally {
        setLoading(false);
      }
    })();
  }, [apiUrl, token, queryCompany, selectedMonth, selectedRegionId, selectedMonthInfo.startStr, selectedMonthInfo.endStr]);

  const receiptsInRegion = useMemo(() => {
    if (!selectedRegionId.trim()) return [];
    const regionDistIds = new Set(distributors.map((d) => d.id));
    const teamIds = new Set(teamColumns.map((c) => c.teamId));
    return receipts.filter((r) => {
      if (r.distributorId && regionDistIds.has(r.distributorId)) return true;
      const teamId = r.teamId || r.team?.id;
      return !!(teamId && teamIds.has(teamId));
    });
  }, [receipts, teamColumns, selectedRegionId, distributors]);

  const leftoverReceipts = useMemo(() => {
    const orphanKeys = new Set(
      teamColumns.filter((c) => c.orphan).map((c) => teamColKey(c.distributorId, c.teamId))
    );
    if (orphanKeys.size === 0) return [];
    return receiptsInRegion
      .filter((r) => {
        const key = receiptTeamColKey(r, firstDistributorByTeamId);
        return !!(key && orphanKeys.has(key));
      })
      .slice()
      .sort((a, b) => {
        const batch = (a.batchNo || "").localeCompare(b.batchNo || "");
        if (batch !== 0) return batch;
        return String(a.outAt).localeCompare(String(b.outAt));
      });
  }, [receiptsInRegion, teamColumns, firstDistributorByTeamId]);

  const products = useMemo(() => {
    // Keep product ordering consistent with Products page (displayOrder from /products)
    const idsInReceipts = new Set<string>();
    receiptsInRegion.forEach((r) => {
      (r.lines || []).forEach((line) => {
        if (line.productId) idsInReceipts.add(line.productId);
      });
    });
    return allProducts.filter((p) => idsInReceipts.has(p.id));
  }, [allProducts, receiptsInRegion]);

  const quantityByProductTeam = useMemo(() => {
    const map = new Map<string, Map<string, number>>();
    receiptsInRegion.forEach((r) => {
      const teamId = r.teamId || r.team?.id;
      if (!teamId) return;
      (r.lines || []).forEach((line) => {
        if (!line.productId || line.quantity == null || line.quantity <= 0) return;
        if (!map.has(line.productId)) map.set(line.productId, new Map());
        const teamMap = map.get(line.productId)!;
        teamMap.set(teamId, (teamMap.get(teamId) ?? 0) + line.quantity);
      });
    });
    return map;
  }, [receiptsInRegion]);

  const totalByTeam = useMemo(() => {
    const map = new Map<string, number>();
    teamColumns.forEach((col) => {
      let sum = 0;
      products.forEach((p) => {
        sum += quantityByProductTeam.get(p.id)?.get(col.teamId) ?? 0;
      });
      map.set(col.teamId, sum);
    });
    return map;
  }, [teamColumns, products, quantityByProductTeam]);

  const totalStockInByProduct = useMemo(() => {
    const inPlusByProduct = new Map<string, number>();
    stockInReceipts.forEach((r) => {
      const isExchange = (r.stockInType || "").toLowerCase().includes("exchange");
      if (isExchange) return;
      (r.lines || []).forEach((line) => {
        if (!line.productId || line.quantity == null) return;
        inPlusByProduct.set(line.productId, (inPlusByProduct.get(line.productId) ?? 0) + line.quantity);
      });
    });

    const result = new Map<string, number>();
    const productIds = new Set<string>([
      ...Object.keys(openingByProduct),
      ...inPlusByProduct.keys(),
      ...products.map((p) => p.id),
    ]);
    productIds.forEach((productId) => {
      result.set(productId, (openingByProduct[productId] ?? 0) + (inPlusByProduct.get(productId) ?? 0));
    });
    return result;
  }, [stockInReceipts, openingByProduct, products]);

  const productsByType = useMemo(() => {
    const map = new Map<string, { label: string; products: ProductRow[] }>();
    products.forEach((p) => {
      const typeName = (productIdToType[p.id] || "").trim() || "Other";
      if (!map.has(typeName)) map.set(typeName, { label: typeName, products: [] });
      map.get(typeName)!.products.push(p);
    });
    return map;
  }, [products, productIdToType]);

  const orderedProductTypes = useMemo(() => {
    const entries = Array.from(productsByType.entries());
    const ordered: [string, { label: string; products: ProductRow[] }][] = [];
    for (const name of PRODUCT_TYPE_ORDER) {
      const found = entries.find(([, v]) => v.label.trim().toLowerCase() === name.toLowerCase());
      if (found) ordered.push(found);
    }
    for (const e of entries) {
      if (!PRODUCT_TYPE_ORDER.some((n) => n.toLowerCase() === e[1].label.trim().toLowerCase())) ordered.push(e);
    }
    return ordered;
  }, [productsByType]);

  const filteredAndSortedProductTypes = useMemo(() => {
    let list = orderedProductTypes;
    if (selectedProductTypeFilters.length > 0) {
      const set = new Set(selectedProductTypeFilters.map((s) => s.trim().toLowerCase()));
      list = list.filter(([, v]) => set.has(v.label.trim().toLowerCase()));
    }
    if (productTypeCustomOrder.length > 0) {
      const orderMap = new Map(productTypeCustomOrder.map((label, i) => [label.trim().toLowerCase(), i]));
      list = [...list].sort((a, b) => {
        const ia = orderMap.get(a[1].label.trim().toLowerCase()) ?? 1e9;
        const ib = orderMap.get(b[1].label.trim().toLowerCase()) ?? 1e9;
        return ia - ib;
      });
    }
    return list;
  }, [orderedProductTypes, selectedProductTypeFilters, productTypeCustomOrder]);

  const sortMenuOrder = useMemo(() => {
    if (productTypeCustomOrder.length > 0) {
      const inCustom = productTypeCustomOrder.filter((label) =>
        orderedProductTypes.some(([, v]) => v.label.trim().toLowerCase() === label.trim().toLowerCase())
      );
      const rest = orderedProductTypes
        .filter(([, v]) => !productTypeCustomOrder.some((c) => c.trim().toLowerCase() === v.label.trim().toLowerCase()))
        .map(([, v]) => v.label);
      return [...inCustom, ...rest];
    }
    return orderedProductTypes.map(([, v]) => v.label);
  }, [orderedProductTypes, productTypeCustomOrder]);

  const moveSortOrder = useCallback((index: number, direction: "up" | "down") => {
    setProductTypeCustomOrder((prev) => {
      const next = [...sortMenuOrder];
      const j = direction === "up" ? index - 1 : index + 1;
      if (j < 0 || j >= next.length) return prev.length > 0 ? prev : [];
      [next[index], next[j]] = [next[j], next[index]];
      return next;
    });
  }, [sortMenuOrder]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_FILTER, JSON.stringify(selectedProductTypeFilters));
    } catch {
      // ignore
    }
  }, [selectedProductTypeFilters]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_SORT, JSON.stringify(productTypeCustomOrder));
    } catch {
      // ignore
    }
  }, [productTypeCustomOrder]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_MONTH, selectedMonth);
    } catch {
      // ignore
    }
  }, [selectedMonth]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_REGION, selectedRegionId);
    } catch {
      // ignore
    }
  }, [selectedRegionId]);

  const tableWidth =
    COL_NO_PX +
    COL_SKU_PX +
    COL_PRODUCT_PX +
    teamColumns.length * COL_TEAM_PX +
    4 * COL_EXTRA_PX;

  const weekHeader = useMemo(() => {
    const line1 = selectedMonthInfo.label;
    const line2 = formatDateRange(selectedMonthInfo.start, selectedMonthInfo.end);
    return { line1, line2, message: `${line1} (${line2})` };
  }, [selectedMonthInfo]);

  const renderTablesForReceipts = (
    subsetReceipts: Receipt[],
    headerLine1: string,
    headerLine2: string,
    emptyText: string
  ) => {
    const weekMap = new Map<number, Receipt[]>();
    subsetReceipts.forEach((r) => {
      const d = new Date(r.outAt);
      if (Number.isNaN(d.getTime())) return;
      const w = getISOWeek(d);
      if (!weekMap.has(w)) weekMap.set(w, []);
      weekMap.get(w)!.push(r);
    });
    const activeWeeks = Array.from(weekMap.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([week, rs]) => {
        const first = rs[0] ? new Date(rs[0].outAt) : selectedMonthInfo.start;
        const { start, end } = getISOWeekRange(first.getFullYear(), week);
        return { week, start, end, receipts: rs };
      });

    const dynamicTableWidth =
      COL_NO_PX +
      COL_SKU_PX +
      COL_PRODUCT_PX +
      activeWeeks.length * teamColumns.length * COL_TEAM_PX +
      4 * COL_EXTRA_PX;

    const idsInSubset = new Set<string>();
    subsetReceipts.forEach((r) => {
      (r.lines || []).forEach((line) => {
        if (line.productId) idsInSubset.add(line.productId);
      });
    });
    const subsetProducts = allProducts.filter((p) => idsInSubset.has(p.id));
    const subsetQuantityByProductWeekTeam = new Map<string, Map<number, Map<string, number>>>();
    subsetReceipts.forEach((r) => {
      const colKey = receiptTeamColKey(r, firstDistributorByTeamId);
      if (!colKey) return;
      const d = new Date(r.outAt);
      if (Number.isNaN(d.getTime())) return;
      const week = getISOWeek(d);
      (r.lines || []).forEach((line) => {
        if (!line.productId || line.quantity == null || line.quantity <= 0) return;
        if (!subsetQuantityByProductWeekTeam.has(line.productId)) subsetQuantityByProductWeekTeam.set(line.productId, new Map());
        const weekMapForProduct = subsetQuantityByProductWeekTeam.get(line.productId)!;
        if (!weekMapForProduct.has(week)) weekMapForProduct.set(week, new Map());
        const teamMap = weekMapForProduct.get(week)!;
        teamMap.set(colKey, (teamMap.get(colKey) ?? 0) + line.quantity);
      });
    });
    const subsetProductsByType = new Map<string, { label: string; products: ProductRow[] }>();
    subsetProducts.forEach((p) => {
      const typeName = (productIdToType[p.id] || "").trim() || "Other";
      if (!subsetProductsByType.has(typeName)) subsetProductsByType.set(typeName, { label: typeName, products: [] });
      subsetProductsByType.get(typeName)!.products.push(p);
    });
    const entries = Array.from(subsetProductsByType.entries());
    const ordered: [string, { label: string; products: ProductRow[] }][] = [];
    for (const name of PRODUCT_TYPE_ORDER) {
      const found = entries.find(([, v]) => v.label.trim().toLowerCase() === name.toLowerCase());
      if (found) ordered.push(found);
    }
    for (const e of entries) {
      if (!PRODUCT_TYPE_ORDER.some((n) => n.toLowerCase() === e[1].label.trim().toLowerCase())) ordered.push(e);
    }
    let subsetFiltered = ordered;
    if (selectedProductTypeFilters.length > 0) {
      const set = new Set(selectedProductTypeFilters.map((s) => s.trim().toLowerCase()));
      subsetFiltered = subsetFiltered.filter(([, v]) => set.has(v.label.trim().toLowerCase()));
    }
    if (productTypeCustomOrder.length > 0) {
      const orderMap = new Map(productTypeCustomOrder.map((label, i) => [label.trim().toLowerCase(), i]));
      subsetFiltered = [...subsetFiltered].sort((a, b) => {
        const ia = orderMap.get(a[1].label.trim().toLowerCase()) ?? 1e9;
        const ib = orderMap.get(b[1].label.trim().toLowerCase()) ?? 1e9;
        return ia - ib;
      });
    }

    if (subsetFiltered.length === 0) {
      return (
        <div className="overflow-auto">
          <Table className="border-b border-slate-200/60 text-xs" style={{ tableLayout: "fixed", width: dynamicTableWidth, minWidth: dynamicTableWidth }}>
            <Table.Thead className="text-xs bg-white dark:bg-darkmode-600">
              <Table.Tr>
                <Table.Td style={colStyle(COL_NO_PX)} className="py-1 px-2 bg-slate-50 dark:bg-darkmode-700 sticky left-0 z-30 border-r-2 border-slate-200 dark:border-darkmode-400" />
                <Table.Td style={colStyle(COL_SKU_PX)} className="py-1 px-2 bg-slate-50 dark:bg-darkmode-700 sticky left-[44px] z-30 border-r border-slate-200 dark:border-darkmode-400" />
                <Table.Td style={colStyle(COL_PRODUCT_PX)} className="py-1 px-2 bg-slate-50 dark:bg-darkmode-700 sticky left-[134px] z-30 border-r border-slate-200 dark:border-darkmode-400" />
                <Table.Td colSpan={Math.max(activeWeeks.length * Math.max(teamColumns.length, 1), 1)} className="py-1 px-2 text-center font-medium bg-primary/10 dark:bg-primary/10 text-[11px] border-r border-slate-200 dark:border-darkmode-400">
                  <div className="leading-tight">{headerLine1}</div>
                  <div className="text-[10px] opacity-90 mt-0.5">{headerLine2}</div>
                </Table.Td>
                <Table.Td style={colStyle(COL_EXTRA_PX)} className="py-1 px-2 bg-slate-100 dark:bg-darkmode-700 border-r border-slate-200 dark:border-darkmode-400" />
                <Table.Td style={colStyle(COL_EXTRA_PX)} className="py-1 px-2 bg-slate-100 dark:bg-darkmode-700 border-r border-slate-200 dark:border-darkmode-400" />
                <Table.Td style={colStyle(COL_EXTRA_PX)} className="py-1 px-2 bg-slate-100 dark:bg-darkmode-700 border-r border-slate-200 dark:border-darkmode-400" />
                <Table.Td style={colStyle(COL_EXTRA_PX)} className="py-1 px-2 bg-slate-100 dark:bg-darkmode-700 border-r border-slate-200 dark:border-darkmode-400" />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <Table.Tr>
                <Table.Td colSpan={3 + activeWeeks.length * teamColumns.length + 4} className="py-8 text-center text-slate-500 dark:text-slate-400 border-dashed dark:bg-darkmode-600">
                  {emptyText}
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </div>
      );
    }

    return (
      <>
        {subsetFiltered.map(([, { label, products: typeProducts }]) => {
          const teamColumnsForType = teamColumns.filter((col) =>
            typeProducts.some((p) => {
              const weekMap = subsetQuantityByProductWeekTeam.get(p.id);
              if (!weekMap) return false;
              const colKey = teamColKey(col.distributorId, col.teamId);
              for (const teamMap of weekMap.values()) {
                if ((teamMap.get(colKey) ?? 0) > 0) return true;
              }
              return false;
            })
          );
          const activeWeeksForType = activeWeeks.filter((wk) =>
            typeProducts.some((p) => {
              const weekMap = subsetQuantityByProductWeekTeam.get(p.id);
              if (!weekMap) return false;
              const teamMap = weekMap.get(wk.week);
              if (!teamMap) return false;
              for (const qty of teamMap.values()) {
                if (qty > 0) return true;
              }
              return false;
            })
          );
          const distributorSpansForType = (() => {
            const colKeysForType = new Set(teamColumnsForType.map((c) => teamColKey(c.distributorId, c.teamId)));
            const spans: { name: string; span: number }[] = [];
            for (const { name } of distributorSpans) {
              const distTeams = teamColumns.filter((c) => c.distributorName === name);
              const count = distTeams.filter((c) => colKeysForType.has(teamColKey(c.distributorId, c.teamId))).length;
              if (count > 0) spans.push({ name, span: count });
            }
            return spans;
          })();
          const dynamicTableWidthForType =
            COL_NO_PX +
            COL_SKU_PX +
            COL_PRODUCT_PX +
            activeWeeksForType.length * Math.max(teamColumnsForType.length, 1) * COL_TEAM_PX +
            4 * COL_EXTRA_PX;
          const outByProduct = new Map<string, number>();
          const returnedByProduct = new Map<string, number>();
          typeProducts.forEach((p) => {
            let out = 0;
            let returned = 0;
            activeWeeksForType.forEach((wk) => {
              teamColumnsForType.forEach((col) => {
                out += subsetQuantityByProductWeekTeam.get(p.id)?.get(wk.week)?.get(teamColKey(col.distributorId, col.teamId)) ?? 0;
              });
              wk.receipts.forEach((r) => {
                (r.lines || []).forEach((line) => {
                  if (line.productId !== p.id || line.quantity == null || line.quantity >= 0) return;
                  returned += Math.abs(line.quantity);
                });
              });
            });
            outByProduct.set(p.id, out);
            returnedByProduct.set(p.id, returned);
          });
          const typeTotalByWeekTeam = new Map<number, Map<string, number>>();
          activeWeeksForType.forEach((wk) => {
            const teamTotal = new Map<string, number>();
            teamColumnsForType.forEach((col) => {
              const colKey = teamColKey(col.distributorId, col.teamId);
              const sum = typeProducts.reduce((s, p) => s + (subsetQuantityByProductWeekTeam.get(p.id)?.get(wk.week)?.get(colKey) ?? 0), 0);
              teamTotal.set(colKey, sum);
            });
            typeTotalByWeekTeam.set(wk.week, teamTotal);
          });
          return (
            <div key={label} className="overflow-auto rounded-lg border border-slate-200/60 dark:border-darkmode-400 bg-white dark:bg-darkmode-800">
              <div className="px-4 py-2.5 border-b border-slate-200/60 dark:border-darkmode-400 bg-slate-50 dark:bg-darkmode-700 font-semibold text-slate-700 dark:text-slate-300 text-sm">{label}</div>
              <Table className="border-b-0 text-xs" style={{ tableLayout: "fixed", width: dynamicTableWidthForType, minWidth: dynamicTableWidthForType }}>
                <Table.Thead className="text-xs bg-white dark:bg-darkmode-600">
                  <Table.Tr>
                    <Table.Td style={colStyle(COL_NO_PX)} className="py-1 px-2 bg-slate-50 dark:bg-darkmode-700 sticky left-0 z-30 border-r-2 border-slate-200 dark:border-darkmode-400" />
                    <Table.Td style={colStyle(COL_SKU_PX)} className="py-1 px-2 bg-slate-50 dark:bg-darkmode-700 sticky left-[44px] z-30 border-r border-slate-200 dark:border-darkmode-400" />
                    <Table.Td style={colStyle(COL_PRODUCT_PX)} className="py-1 px-2 bg-slate-50 dark:bg-darkmode-700 sticky left-[134px] z-30 border-r border-slate-200 dark:border-darkmode-400" />
                    {activeWeeksForType.map((wk) => (
                      <Table.Td key={`wk-head-${wk.week}`} colSpan={Math.max(teamColumnsForType.length, 1)} className="py-1 px-2 text-center font-medium bg-primary/10 dark:bg-primary/10 text-[11px] border-r border-slate-200 dark:border-darkmode-400">
                        <div className="leading-tight">Week {wk.week}</div>
                        <div className="text-[10px] opacity-90 mt-0.5">{formatDateRange(wk.start, wk.end)}</div>
                      </Table.Td>
                    ))}
                    <Table.Td style={colStyle(COL_EXTRA_PX)} className="py-1 px-2 bg-slate-100 dark:bg-darkmode-700 border-r border-slate-200 dark:border-darkmode-400" />
                    <Table.Td style={colStyle(COL_EXTRA_PX)} className="py-1 px-2 bg-slate-100 dark:bg-darkmode-700 border-r border-slate-200 dark:border-darkmode-400" />
                    <Table.Td style={colStyle(COL_EXTRA_PX)} className="py-1 px-2 bg-slate-100 dark:bg-darkmode-700 border-r border-slate-200 dark:border-darkmode-400" />
                    <Table.Td style={colStyle(COL_EXTRA_PX)} className="py-1 px-2 bg-slate-100 dark:bg-darkmode-700 border-r border-slate-200 dark:border-darkmode-400" />
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td style={colStyle(COL_NO_PX)} className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 sticky left-0 z-30 border-r-2 border-slate-200 dark:border-darkmode-400">NO</Table.Td>
                    <Table.Td style={colStyle(COL_SKU_PX)} className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 sticky left-[44px] z-30 border-r border-slate-200 dark:border-darkmode-400">SKU ITEM</Table.Td>
                    <Table.Td style={colStyle(COL_PRODUCT_PX)} className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 sticky left-[134px] z-30 border-r border-slate-200 dark:border-darkmode-400">PRODUCT NAME</Table.Td>
                    {activeWeeksForType.map((wk) => (
                      distributorSpansForType.map(({ name, span }) => (
                        <Table.Td key={`wk-dist-${wk.week}-${name}`} colSpan={span} style={{ width: span * COL_TEAM_PX, minWidth: span * COL_TEAM_PX }} className="py-2 px-1 font-medium text-center bg-slate-50/70 dark:bg-darkmode-700/30 text-[11px] border-r border-slate-200 dark:border-darkmode-400">{name}</Table.Td>
                      ))
                    ))}
                    <Table.Td style={colStyle(COL_EXTRA_PX)} className="py-2 px-1 font-medium text-center bg-slate-100 dark:bg-darkmode-700 text-[11px] border-r border-slate-200 dark:border-darkmode-400">RETURN (+)</Table.Td>
                    <Table.Td style={colStyle(COL_EXTRA_PX)} className="py-2 px-1 font-medium text-center bg-slate-100 dark:bg-darkmode-700 text-[11px] border-r border-slate-200 dark:border-darkmode-400">CLOSING STOCK</Table.Td>
                    <Table.Td style={colStyle(COL_EXTRA_PX)} className="py-2 px-1 font-medium text-center bg-slate-100 dark:bg-darkmode-700 text-[11px] border-r border-slate-200 dark:border-darkmode-400">UNIT PRICE (RM)</Table.Td>
                    <Table.Td style={colStyle(COL_EXTRA_PX)} className="py-2 px-1 font-medium text-center bg-slate-100 dark:bg-darkmode-700 text-[11px] border-r border-slate-200 dark:border-darkmode-400">TOTAL COST</Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td style={colStyle(COL_NO_PX)} className="py-1.5 px-2 bg-slate-50 dark:bg-darkmode-700 sticky left-0 z-30 border-r-2 border-slate-200 dark:border-darkmode-400" />
                    <Table.Td style={colStyle(COL_SKU_PX)} className="py-1.5 px-2 bg-slate-50 dark:bg-darkmode-700 sticky left-[44px] z-30 border-r border-slate-200 dark:border-darkmode-400" />
                    <Table.Td style={colStyle(COL_PRODUCT_PX)} className="py-1.5 px-2 bg-slate-50 dark:bg-darkmode-700 sticky left-[134px] z-30 border-r border-slate-200 dark:border-darkmode-400" />
                    {activeWeeksForType.map((wk) => (
                      teamColumnsForType.map((col) => {
                        const colKey = teamColKey(col.distributorId, col.teamId);
                        const receiptsForCell = wk.receipts.filter((r) => receiptTeamColKey(r, firstDistributorByTeamId) === colKey);
                        const viewQuery = companyIdFromUrl ? `?companyId=${companyIdFromUrl}` : "";
                        return (
                          <Table.Td key={`wk-team-${wk.week}-${colKey}`} style={colStyle(COL_TEAM_PX)} className={`py-1.5 px-1 font-medium text-center text-[11px] border-r border-slate-200 dark:border-darkmode-400 overflow-hidden ${col.orphan ? "bg-amber-50 dark:bg-amber-900/20" : "bg-slate-50/70 dark:bg-darkmode-700/30"}`}>
                            <div className="flex flex-col items-center gap-0.5 min-w-0 w-full">
                              <span className="text-[10px] text-slate-600 dark:text-slate-400 break-words text-center leading-tight" title={col.teamName}>{col.teamName}</span>
                              {col.orphan ? (
                                <span className="text-[9px] font-normal text-amber-700 dark:text-amber-400 leading-tight text-center" title="Team is not on this distributor's current supply list">
                                  not in supply list
                                </span>
                              ) : null}
                              {receiptsForCell.length > 0 ? (
                                receiptsForCell.map((rec) => {
                                  const outDateLabel = new Date(rec.outAt).toLocaleDateString("en-GB", {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "2-digit",
                                  });
                                  return (
                                    <Link
                                      key={rec.id}
                                      to={`/stock-out/${rec.id}${viewQuery}`}
                                      className="text-primary hover:underline font-medium whitespace-nowrap"
                                      title="View / edit Stock OUT"
                                    >
                                      {outDateLabel}
                                    </Link>
                                  );
                                })
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </div>
                          </Table.Td>
                        );
                      })
                    ))}
                    <Table.Td style={colStyle(COL_EXTRA_PX)} className="py-1.5 px-2 bg-slate-100 dark:bg-darkmode-700 border-r border-slate-200 dark:border-darkmode-400" />
                    <Table.Td style={colStyle(COL_EXTRA_PX)} className="py-1.5 px-2 bg-slate-100 dark:bg-darkmode-700 border-r border-slate-200 dark:border-darkmode-400" />
                    <Table.Td style={colStyle(COL_EXTRA_PX)} className="py-1.5 px-2 bg-slate-100 dark:bg-darkmode-700 border-r border-slate-200 dark:border-darkmode-400" />
                    <Table.Td style={colStyle(COL_EXTRA_PX)} className="py-1.5 px-2 bg-slate-100 dark:bg-darkmode-700 border-r border-slate-200 dark:border-darkmode-400" />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {typeProducts.map((p, idx) => (
                    <Table.Tr key={p.id} className="[&_td]:border-b [&_td]:border-slate-200/60">
                      <Table.Td style={colStyle(COL_NO_PX)} className="py-2 px-2 bg-slate-50 dark:bg-darkmode-700 tabular-nums sticky left-0 z-20 border-r-2 border-slate-200 dark:border-darkmode-400">{idx + 1}</Table.Td>
                      <Table.Td style={colStyle(COL_SKU_PX)} className="py-2 px-2 bg-slate-50 dark:bg-darkmode-700 overflow-hidden sticky left-[44px] z-20 border-r border-slate-200 dark:border-darkmode-400">
                        <span className="block min-w-0 truncate text-[11px]" title={p.sku || "—"}>{p.sku || "—"}</span>
                      </Table.Td>
                      <Table.Td style={colStyle(COL_PRODUCT_PX)} className="py-2 px-2 bg-slate-50 dark:bg-darkmode-700 overflow-hidden sticky left-[134px] z-20 border-r border-slate-200 dark:border-darkmode-400">
                        <span className="block min-w-0 truncate text-[11px]" title={p.name || "—"}>{p.name || "—"}</span>
                      </Table.Td>
                      {activeWeeksForType.map((wk) => (
                        teamColumnsForType.map((col) => (
                          <Table.Td key={`wk-val-${wk.week}-${teamColKey(col.distributorId, col.teamId)}-${p.id}`} style={colStyle(COL_TEAM_PX)} className="py-2 px-2 text-center tabular-nums whitespace-nowrap bg-success/10 dark:bg-success/10 text-[11px] border-r border-slate-200 dark:border-darkmode-400">
                            {formatIntOrDash(subsetQuantityByProductWeekTeam.get(p.id)?.get(wk.week)?.get(teamColKey(col.distributorId, col.teamId)) ?? 0)}
                          </Table.Td>
                        ))
                      ))}
                      <Table.Td style={colStyle(COL_EXTRA_PX)} className="py-2 px-2 text-center tabular-nums bg-slate-50 dark:bg-darkmode-700 text-[11px] border-r border-slate-200 dark:border-darkmode-400">
                        {formatIntOrDash(returnedByProduct.get(p.id) ?? 0)}
                      </Table.Td>
                      <Table.Td style={colStyle(COL_EXTRA_PX)} className="py-2 px-2 text-center tabular-nums bg-slate-50 dark:bg-darkmode-700 text-[11px] border-r border-slate-200 dark:border-darkmode-400">
                        {formatIntOrDash((totalStockInByProduct.get(p.id) ?? 0) - (outByProduct.get(p.id) ?? 0) + (returnedByProduct.get(p.id) ?? 0))}
                      </Table.Td>
                      <Table.Td style={colStyle(COL_EXTRA_PX)} className="py-2 px-2 text-center bg-slate-50 dark:bg-darkmode-700 text-[11px] border-r border-slate-200 dark:border-darkmode-400">—</Table.Td>
                      <Table.Td style={colStyle(COL_EXTRA_PX)} className="py-2 px-2 text-center bg-slate-50 dark:bg-darkmode-700 text-[11px] border-r border-slate-200 dark:border-darkmode-400">—</Table.Td>
                    </Table.Tr>
                  ))}
                  <Table.Tr className="[&_td]:border-b-0 [&_td]:border-t-2 [&_td]:border-t-slate-400 dark:[&_td]:border-t-darkmode-400 [&_td]:border-slate-200/60 bg-white dark:bg-darkmode-600 font-medium">
                    <Table.Td style={colStyle(COL_NO_PX)} className="py-2 px-2 bg-white dark:bg-darkmode-600 sticky left-0 z-20 border-r-2 border-slate-200 dark:border-darkmode-400" />
                    <Table.Td style={colStyle(COL_SKU_PX)} className="py-2 px-2 bg-white dark:bg-darkmode-600 sticky left-[44px] z-20 border-r border-slate-200 dark:border-darkmode-400" />
                    <Table.Td style={colStyle(COL_PRODUCT_PX)} className="py-2 px-2 bg-white dark:bg-darkmode-600 sticky left-[134px] z-20 border-r border-slate-200 dark:border-darkmode-400" />
                    {activeWeeksForType.map((wk) => (
                      teamColumnsForType.map((col) => (
                        <Table.Td key={`wk-total-${wk.week}-${teamColKey(col.distributorId, col.teamId)}`} style={colStyle(COL_TEAM_PX)} className="py-2 px-2 text-center tabular-nums whitespace-nowrap bg-white dark:bg-darkmode-600 text-[11px] font-bold border-r border-slate-200 dark:border-darkmode-400">
                          {formatIntOrDash(typeTotalByWeekTeam.get(wk.week)?.get(teamColKey(col.distributorId, col.teamId)) ?? 0)}
                        </Table.Td>
                      ))
                    ))}
                    <Table.Td style={colStyle(COL_EXTRA_PX)} className="py-2 px-2 text-center tabular-nums bg-white dark:bg-darkmode-600 text-[11px] border-r border-slate-200 dark:border-darkmode-400">
                      {formatIntOrDash(typeProducts.reduce((sum, p) => sum + (returnedByProduct.get(p.id) ?? 0), 0))}
                    </Table.Td>
                    <Table.Td style={colStyle(COL_EXTRA_PX)} className="py-2 px-2 text-center tabular-nums bg-white dark:bg-darkmode-600 text-[11px] border-r border-slate-200 dark:border-darkmode-400">
                      {formatIntOrDash(typeProducts.reduce((sum, p) => sum + (totalStockInByProduct.get(p.id) ?? 0) - (outByProduct.get(p.id) ?? 0) + (returnedByProduct.get(p.id) ?? 0), 0))}
                    </Table.Td>
                    <Table.Td style={colStyle(COL_EXTRA_PX)} className="py-2 px-2 text-center bg-white dark:bg-darkmode-600 text-[11px] border-r border-slate-200 dark:border-darkmode-400">—</Table.Td>
                    <Table.Td style={colStyle(COL_EXTRA_PX)} className="py-2 px-2 text-center bg-white dark:bg-darkmode-600 text-[11px] border-r border-slate-200 dark:border-darkmode-400">—</Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>
            </div>
          );
        })}
      </>
    );
  };

  return (
    <ProtectedRoute>
      <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
        <div className="grid grid-cols-12 gap-y-10 gap-x-6 w-full">
          <PageLoadingOverlay show={loading && Boolean(selectedRegionId.trim())} label="Loading stock out…" />
          <div className="col-span-12">
            <div className="flex flex-col md:h-10 gap-y-3 md:items-center md:flex-row">
              <div className="text-base font-medium group-[.mode--light]:text-white">
                Stock OUT
              </div>
              <div className="flex flex-col sm:flex-row gap-x-3 gap-y-2 md:ml-auto">
                <Button
                  variant="primary"
                  className="group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box"
                  onClick={() => navigate(addUrl)}
                >
                  <Lucide icon="PenLine" className="stroke-[1.3] w-4 h-4 mr-2" />
                  Add New Stock OUT
                </Button>
              </div>
            </div>

            <div className="mt-3.5">
              <div className="flex flex-col box box--stacked">
                <div className="relative z-[100] flex flex-col p-5 sm:items-center sm:flex-row gap-y-2 gap-x-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-600 dark:text-slate-400 shrink-0">Month</span>
                    <Menu className="relative inline-block">
                      <Menu.Button className="relative inline-flex items-center w-full min-w-[180px] sm:w-[220px] pl-9 pr-8 py-2 rounded-[0.5rem] border border-slate-300/60 dark:border-darkmode-400 bg-white dark:bg-darkmode-800 text-slate-700 dark:text-slate-300 text-left text-sm hover:bg-slate-50 dark:hover:bg-darkmode-700 transition-colors">
                        <Lucide icon="Calendar" className="absolute left-0 inset-y-0 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500 pointer-events-none" />
                        <span className="truncate">{selectedMonthInfo.label}</span>
                        <Lucide icon="ChevronDown" className="absolute right-0 w-4 h-4 my-auto mr-2 stroke-[1.3] text-slate-400 pointer-events-none" />
                      </Menu.Button>
                      <Menu.Items className="absolute left-0 mt-1 z-[100] w-56 rounded-[0.6rem] border border-slate-200/60 dark:border-darkmode-400 bg-white dark:bg-darkmode-800 shadow-lg py-2" style={{ zIndex: 9999 }}>
                        <div className="flex items-center justify-between px-3 py-2 gap-2">
                          <button type="button" onClick={(e) => { e.preventDefault(); setPickerYear((y) => y - 1); }} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-darkmode-600">
                            <Lucide icon="ChevronLeft" className="w-4 h-4" />
                          </button>
                          <span className="text-sm font-medium tabular-nums">{pickerYear}</span>
                          <button type="button" onClick={(e) => { e.preventDefault(); setPickerYear((y) => y + 1); }} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-darkmode-600">
                            <Lucide icon="ChevronRight" className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="grid grid-cols-3 gap-1 px-2 pb-2">
                          {MONTH_NAMES.map((name, i) => {
                            const value = `${pickerYear}-${String(i + 1).padStart(2, "0")}`;
                            const isSelected = selectedMonth === value;
                            return (
                              <Menu.Item key={value}>
                                {({ close }) => (
                                  <button type="button" onClick={() => { setSelectedMonth(value); close(); }} className={`py-2 px-2 rounded text-xs font-medium transition-colors ${isSelected ? "bg-primary text-white dark:bg-primary" : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-darkmode-600"}`}>
                                    {name}
                                  </button>
                                )}
                              </Menu.Item>
                            );
                          })}
                        </div>
                      </Menu.Items>
                    </Menu>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-600 dark:text-slate-400 shrink-0">Region</span>
                    <Menu className="relative inline-block">
                      <Menu.Button className="relative inline-flex items-center w-full min-w-[180px] sm:w-[220px] pl-9 pr-8 py-2 rounded-[0.5rem] border border-slate-300/60 dark:border-darkmode-400 bg-white dark:bg-darkmode-800 text-slate-700 dark:text-slate-300 text-left text-sm hover:bg-slate-50 dark:hover:bg-darkmode-700 transition-colors">
                        <Lucide icon="MapPin" className="absolute left-0 inset-y-0 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500 pointer-events-none" />
                        <span className="truncate">{selectedRegionId ? (regions.find((r) => r.id === selectedRegionId)?.name ?? "Select region...") : "Select region..."}</span>
                        <Lucide icon="ChevronDown" className="absolute right-0 w-4 h-4 my-auto mr-2 stroke-[1.3] text-slate-400 pointer-events-none" />
                      </Menu.Button>
                      <Menu.Items className="absolute left-0 mt-1 z-[100] w-56 rounded-[0.6rem] border border-slate-200/60 dark:border-darkmode-400 bg-white dark:bg-darkmode-800 shadow-lg py-2 max-h-64 overflow-y-auto" style={{ zIndex: 9999 }}>
                        {regions.map((r) => (
                          <Menu.Item key={r.id}>
                            {({ close }) => (
                              <button
                                type="button"
                                onClick={() => { setSelectedRegionId(r.id); close(); }}
                                className={`w-full text-left px-3 py-2 text-sm ${selectedRegionId === r.id ? "bg-primary text-white dark:bg-primary" : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-darkmode-600"}`}
                              >
                                {r.name}
                              </button>
                            )}
                          </Menu.Item>
                        ))}
                        {regions.length === 0 && (
                          <div className="px-3 py-2 text-sm text-slate-500">No regions available</div>
                        )}
                      </Menu.Items>
                    </Menu>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
                    <Menu className="relative inline-block">
                      <Menu.Button className="relative inline-flex items-center min-w-[90px] w-auto pl-9 pr-3 py-2 rounded-[0.5rem] border border-slate-300/60 dark:border-darkmode-400 bg-white dark:bg-darkmode-800 text-slate-700 dark:text-slate-300 text-left text-sm whitespace-nowrap hover:bg-slate-50 dark:hover:bg-darkmode-700">
                        <Lucide icon="Filter" className="absolute left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500" />
                        Filter
                        {selectedProductTypeFilters.length > 0 && <span className="ml-1 text-xs">({selectedProductTypeFilters.length})</span>}
                      </Menu.Button>
                      <Menu.Items className="w-64 z-[100]" style={{ zIndex: 9999 }}>
                        <div className="p-4">
                          <div className="text-sm font-medium mb-2">Product Type</div>
                          {orderedProductTypes.length > 0 && (
                            <button
                              type="button"
                              className="text-xs text-blue-600 dark:text-blue-400 hover:underline mb-2"
                              onClick={() => {
                                const all = orderedProductTypes.every(([, v]) =>
                                  selectedProductTypeFilters.some((s) => s.trim().toLowerCase() === v.label.trim().toLowerCase())
                                );
                                setSelectedProductTypeFilters(all ? [] : orderedProductTypes.map(([, v]) => v.label));
                              }}
                            >
                              {orderedProductTypes.every(([, v]) =>
                                selectedProductTypeFilters.some((s) => s.trim().toLowerCase() === v.label.trim().toLowerCase())
                              ) ? "Deselect all" : "Select all"}
                            </button>
                          )}
                          <div className="space-y-2 max-h-48 overflow-y-auto">
                            {orderedProductTypes.map(([, v]) => (
                              <label key={v.label} className="flex items-center cursor-pointer gap-2">
                                <input
                                  type="checkbox"
                                  className="form-check-input rounded border-slate-300"
                                  checked={selectedProductTypeFilters.some((s) => s.trim().toLowerCase() === v.label.trim().toLowerCase())}
                                  onChange={(e) => {
                                    if (e.target.checked) setSelectedProductTypeFilters([...selectedProductTypeFilters, v.label]);
                                    else setSelectedProductTypeFilters(selectedProductTypeFilters.filter((s) => s.trim().toLowerCase() !== v.label.trim().toLowerCase()));
                                  }}
                                />
                                <span className="text-xs">{v.label}</span>
                              </label>
                            ))}
                          </div>
                          {selectedProductTypeFilters.length > 0 && (
                            <button type="button" className="w-full mt-3 px-3 py-1.5 text-xs bg-slate-200 dark:bg-darkmode-700 rounded" onClick={() => setSelectedProductTypeFilters([])}>Clear Filters</button>
                          )}
                        </div>
                      </Menu.Items>
                    </Menu>
                    <Menu className="relative inline-block">
                      <Menu.Button className="relative inline-flex items-center min-w-[90px] w-auto pl-9 pr-3 py-2 rounded-[0.5rem] border border-slate-300/60 dark:border-darkmode-400 bg-white dark:bg-darkmode-800 text-slate-700 dark:text-slate-300 text-left text-sm whitespace-nowrap hover:bg-slate-50 dark:hover:bg-darkmode-700">
                        <Lucide icon="ArrowUpDown" className="absolute left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500" />
                        Sort
                        {productTypeCustomOrder.length > 0 && <span className="ml-1 text-xs">(Custom)</span>}
                      </Menu.Button>
                      <Menu.Items className="w-64 z-[100]" style={{ zIndex: 9999 }}>
                        <div className="p-4">
                          <div className="text-sm font-medium mb-2">Display order</div>
                          <div className="space-y-1 max-h-48 overflow-y-auto">
                            {sortMenuOrder.map((label, index) => (
                              <div key={label} className="flex items-center justify-between gap-2 py-1.5 px-2 rounded hover:bg-slate-100 dark:hover:bg-darkmode-600">
                                <span className="text-xs truncate flex-1">{index + 1}. {label}</span>
                                <div className="flex shrink-0 gap-0.5">
                                  <button type="button" className="p-1 rounded hover:bg-slate-200 disabled:opacity-40 disabled:pointer-events-none" title="Move up" disabled={index === 0} onClick={() => moveSortOrder(index, "up")}>
                                    <Lucide icon="ChevronUp" className="w-3.5 h-3.5" />
                                  </button>
                                  <button type="button" className="p-1 rounded hover:bg-slate-200 disabled:opacity-40 disabled:pointer-events-none" title="Move down" disabled={index === sortMenuOrder.length - 1} onClick={() => moveSortOrder(index, "down")}>
                                    <Lucide icon="ChevronDown" className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                          {productTypeCustomOrder.length > 0 && (
                            <button type="button" className="w-full mt-3 px-3 py-1.5 text-xs bg-slate-200 dark:bg-darkmode-700 rounded" onClick={() => setProductTypeCustomOrder([])}>Reset to default</button>
                          )}
                        </div>
                      </Menu.Items>
                    </Menu>
                  </div>
                </div>

                {!selectedRegionId.trim() ? (
                  <div className="p-10 flex items-center justify-center gap-2 text-slate-500 dark:text-slate-400">
                    Please select a Region to view Stock OUT data.
                  </div>
                ) : (
                  <div className="relative z-10 px-5 pb-5 space-y-8">
                    {renderTablesForReceipts(receiptsInRegion, weekHeader.line1, weekHeader.line2, `No stock out for ${weekHeader.message}. Add Stock OUT records to see data.`)}
                    {leftoverReceipts.length > 0 ? (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-900/10 p-4">
                        <div className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                          Stock OUT not in the current supply list ({leftoverReceipts.length})
                        </div>
                        <div className="text-xs text-amber-800/80 dark:text-amber-300/80 mt-1 mb-3">
                          These receipts belong to a distributor in this region, but the team is no longer on that distributor&apos;s Stocks Supply To. They still count toward HQ stock.
                        </div>
                        <ul className="space-y-1.5">
                          {leftoverReceipts.map((rec) => {
                            const viewQuery = companyIdFromUrl ? `?companyId=${companyIdFromUrl}` : "";
                            const distName =
                              distributors.find((d) => d.id === rec.distributorId)?.name ||
                              "Unknown distributor";
                            const teamName = rec.team?.name || "Unknown team";
                            const dateLabel = new Date(rec.outAt).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            });
                            return (
                              <li key={rec.id} className="text-sm text-slate-800 dark:text-slate-200">
                                <Link
                                  to={`/stock-out/${rec.id}${viewQuery}`}
                                  className="text-primary hover:underline font-medium"
                                >
                                  {rec.batchNo || "Stock OUT"}
                                </Link>
                                <span className="text-slate-500 dark:text-slate-400">
                                  {" "}
                                  · {dateLabel} · {distName} · {teamName}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </RoleGate>
    </ProtectedRoute>
  );
}
